import {CanActivateFn, Router, Routes} from '@angular/router';
import {AboutUsComponent, LoginPageComponent, SignInPageComponent} from "./pages";
import {PasswordResetComponent} from "./pages/auth";
import {ProfileComponent} from "./pages";
import {OverviewComponent, UserArtworksComponent, BlogsComponent, SoldedArtworksComponent, MessagesComponent, EvaluationsComponent, AddressesComponent} from "./pages/front-office/profile";
import {inject} from "@angular/core";
import {AuthenticationService} from "@services";
import {CredentialsComponent} from "./pages/front-office/profile/credentials/credentials.component";
import {
  EvaluationDetailsComponent
} from "./pages/front-office/profile/evaluations/evaluation-details/evaluation-details.component";
import {HomeComponent} from "./pages/front-office/home/home.component";
import {CvComponent} from "./pages/front-office/profile/cv/cv.component";
import {SubscriptionComponent} from "./pages/front-office/profile/subscription/subscription.component";
import {ArtistHomePageComponent} from "./pages/front-office/artist-home-page/artist-home-page.component";
import {GalleryHomePageComponent} from "./pages/front-office/gallery-home-page/gallery-home-page.component";
import {CollectorHomePageComponent} from "./pages/front-office/collector-home-page/collector-home-page.component";
import { GalleryArtistsComponent } from './pages/front-office/profile/gallery-artists/gallery-artists.component';
import { OrdersComponent as FrontOfficeOrdersComponent } from './pages/front-office/orders/orders.component';

export const isLogged: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router)
  const token = authService.token();
  if (!token) return router.createUrlTree(['/auth', 'login']);
  return true;
}

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/front-office/front-office.component')
        .then(d => d.FrontOfficeComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: HomeComponent
      },
      {
        path: 'join/artists',
        component: ArtistHomePageComponent
      },
      {
        path: 'join/collectors',
        component: CollectorHomePageComponent
      },
      {
        path: 'join/galleries',
        component: GalleryHomePageComponent
      }
    ]
  },
  {
    path: '',
    loadComponent: () =>
      import('./components/layout/front-office/front-office.component')
        .then(d => d.FrontOfficeComponent),
    children: [
      {
        path: 'about-us',
        component: AboutUsComponent,
      },
      {
        path: 'artworks',
        loadComponent: () =>
          import('./pages/front-office/artworks/artworks.component')
            .then(d => d.ArtworksComponent)
      },
      {
        path: 'artworks/:id',
        loadComponent: () =>
          import('./pages/front-office/artwork/artwork.component')
            .then(d => d.ArtworkComponent)
      },
      {
        path: 'artists/:id',
        loadComponent: () =>
          import('./pages/front-office/artist/artist.component')
            .then(d => d.ArtistComponent)
      },
      {
        path: 'about-us',
        loadComponent: () =>
          import('./pages/front-office/about-us/about-us.component')
            .then(d => d.AboutUsComponent)
      },
      {
        path: 'artists',
        loadComponent: () =>
          import('./pages/front-office/artists/artists.component')
            .then(d => d.ArtistsComponent)
      },
      {
        path: 'galleries',
        loadComponent: () =>
          import('./pages/front-office/artists/artists.component')
            .then(d => d.ArtistsComponent)
      },
      {
        path: 'galleries/:id',
        loadComponent: () =>
          import('./pages/front-office/artist/artist.component')
            .then(d => d.ArtistComponent)
      },
      {
        path: 'blogs',
        loadComponent: () =>
          import('./pages/front-office/blogs/blogs.component')
            .then(d => d.BlogsComponent)
      },
      {
        path: 'faq',
        loadComponent: () =>
          import('./pages/front-office/faq/faq.component')
            .then(d => d.FaqComponent)
      },
      {
        path: 'exchange-refund-policy',
        loadComponent: () =>
          import('./pages/front-office/exchange-refund-policy/exchange-refund-policy.component')
            .then(d => d.ExchangeRefundPolicyComponent)
      },
      {
        path: 'artist-experience',
        loadComponent: () =>
          import('./pages/front-office/artist-experience/artist-experience.component')
            .then(d => d.ArtistExperienceComponent)
      },
      {
        path: 'exhibitions',
        loadComponent: () =>
          import('./pages/front-office/exhibitions/exhibitions.component')
            .then(d => d.ExhibitionsComponent)
      },
      {
        path: 'checkout',
        loadComponent: () =>
          import('./pages/front-office/checkout/checkout.component')
            .then(d => d.CheckoutComponent),
        loadChildren:() => {
          return [
            {
              path: '',
              pathMatch: 'full',
              redirectTo: 'cart'
            },
            {
              path: 'cart',
              loadComponent: () => import('./pages/front-office/checkout/cart-checkout/cart-checkout.component').then(s => s.CartCheckoutComponent)
            },
            {
              path: 'shipping',
              loadComponent: () => import('./pages/front-office/checkout/cart-shipping/cart-shipping.component').then(s => s.CartShippingComponent)
            },
            {
              path: ':id/confirm',
              loadComponent: () => import('./pages/front-office/checkout/confirm-order/confirm-order.component').then(s => s.ConfirmOrderComponent)
            }
          ]
        }
      },
      {
        path: 'profile',
        loadComponent: () => ProfileComponent,
        canActivateChild: [isLogged],
        canActivate: [isLogged],
        loadChildren: () => [
          {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
          },
          {
            path: 'overview',
            loadComponent: () => OverviewComponent
          },
          {
            path: 'my-artworks',
            loadComponent: () => UserArtworksComponent
          },
          {
            path: 'messages',
            loadComponent: () => MessagesComponent
          },
          {
            path: 'sales',
            loadComponent: () => SoldedArtworksComponent
          },
          {
            path: 'evaluations',
            loadComponent: () => EvaluationsComponent,
            children: [
              {
                path: ':id',
                loadComponent: () => EvaluationDetailsComponent
              }
            ]
          },
          {
            path: 'my-blogs',
            loadComponent: () => BlogsComponent
          },
          {
            path: 'addresses',
            loadComponent: () => AddressesComponent
          },
          {
            path: 'credentials',
            loadComponent: () => CredentialsComponent
          },
          {
            path: 'cv',
            loadComponent: () => CvComponent
          },
          {
            path: 'subscription',
            loadComponent: () => SubscriptionComponent
          },
          {
            path: 'my-artists',
            loadComponent: () => GalleryArtistsComponent
          },
          {
            path: 'orders',
            loadComponent: () => FrontOfficeOrdersComponent
          }
        ]
      }
    ]
  },
  {
    path: 'auth/login',
    loadComponent: () => LoginPageComponent
  },
  {
    path: 'auth/sign-up',
    loadComponent: () => SignInPageComponent
  },
  {
    path: 'auth/password-reset',
    loadComponent: () => PasswordResetComponent
  },
  {
    path: 'auth/password-reset/:token',
    loadComponent: () => PasswordResetComponent
  }
];
